import {
  collection,
  getDocs,
  query,
  where,
  type DocumentData,
  type Firestore,
  type Query,
} from 'firebase/firestore'
import type { ReferenceMonth } from '@shared/date/reference-month'
import type { Invoice } from '@domain/finance'
import type { InvoiceRepository } from '@domain/ports/finance-repository'
import {
  optionalCivilDate,
  optionalText,
  requireCents,
  requireCivilDate,
  requireInteger,
  requireIso,
  requireOneOf,
  requireText,
} from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * As faturas de um mês, para o card da tela de Transações.
 *
 * ## O recorte é por `referenceMonth`, e não por faixa de data
 *
 * O mês de uma fatura foi decidido **na ingestão**, a partir do
 * `billForecastDate` que o banco manda — que cobre 1.860 de 1.864 transações de
 * cartão da base real, e é a autoridade sobre pertinência desde a decisão 137a.
 * `closingDate` está no documento porque a fatura o **mostra**, não porque ele
 * decide de quem ela é. Recortar por faixa de `closingDate` aqui seria uma
 * segunda autoridade sobre a mesma pergunta, livre para discordar da primeira
 * no dia em que o banco deslocar um fechamento — que é exatamente o que ele
 * faz quando o dia cai em fim de semana (decisão 137a).
 *
 * ## A ordenação é em MEMÓRIA, e isso é a escolha barata
 *
 * A consulta é uma igualdade e nada mais. Um `orderBy('dueDate')` junto de um
 * `where('referenceMonth','==')` são **dois campos distintos**, e o Firestore
 * exige índice composto para isso — um índice novo, declarado, deployado, e um
 * bump no guarda de `firestore-indexes.test.ts`. Tudo isso para ordenar o que a
 * base real devolve: **uma** fatura por mês, uma por cartão ativo. Os cards
 * saem ordenados por `dueDate` do mesmo jeito, só que aqui.
 *
 * No dia em que houver dezenas de cartões esta conta muda, e é aqui que ela
 * reabre.
 *
 * ## Só leitura
 *
 * `firestore.rules` nega escrita em `invoices` ao cliente. `totalCents` é
 * **derivado** das transações do ciclo, e um cliente que o escrevesse produziria
 * uma fatura que discorda das próprias linhas.
 */

const STATUSES = ['open', 'closed', 'paid', 'partially_paid'] as const

/**
 * **A lista acima é de runtime, e o `tsc` não a liga sozinho ao tipo.** Mesma
 * armadilha de `CASH_IMPACTS` em `transaction-repository.ts`, e o mesmo fecho:
 * um status que exista no tipo e falte aqui faz **toda fatura naquele estado
 * lançar na leitura**, sem que nenhum teste de tipo aponte. A linha abaixo só
 * compila se os dois conjuntos forem iguais, nos dois sentidos.
 */
type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false
const _statusesCobreAUniao: Equals<Invoice['status'], (typeof STATUSES)[number]> = true
void _statusesCobreAUniao

function invoicesPath(): string {
  return `households/${HOUSEHOLD_ID}/invoices`
}

/**
 * Firestore → domínio.
 *
 * **`totalCents` e `paidCents` entram com o sinal que têm**, sem `absCents` no
 * caminho: os dois são somas de `Transaction.source.amountCents`, e existem para
 * serem comparados um com o outro. Passar um deles por módulo aqui repetiria o
 * erro que a decisão 97(c) pagou — e faria uma fatura normal, que tem os dois
 * negativos, parecer paga em dobro.
 */
export function toInvoice(id: string, data: DocumentData): Invoice {
  const where = `invoices/${id}`
  return {
    id,
    householdId: HOUSEHOLD_ID,
    creditCardId: requireText(data['creditCardId'], `${where}.creditCardId`),
    referenceMonth: requireText(data['referenceMonth'], `${where}.referenceMonth`),
    closingDate: requireCivilDate(data['closingDate'], `${where}.closingDate`),
    dueDate: requireCivilDate(data['dueDate'], `${where}.dueDate`),
    totalCents: requireCents(data['totalCents'], `${where}.totalCents`),
    transactionCount: requireInteger(data['transactionCount'], `${where}.transactionCount`),
    status: requireOneOf(data['status'], STATUSES, `${where}.status`),
    settlementTransactionId: optionalText(data['settlementTransactionId']),
    paidCents: requireCents(data['paidCents'], `${where}.paidCents`),
    paidAt: optionalCivilDate(data['paidAt'], `${where}.paidAt`),
    createdAt: requireIso(data['createdAt'], `${where}.createdAt`),
    updatedAt: requireIso(data['updatedAt'], `${where}.updatedAt`),
  }
}

/**
 * **A construção fica separada da execução** para que `firestore-indexes.test.ts`
 * derive o índice exigido do objeto `Query` que a produção manda, e não de uma
 * cópia dos `where` reescrita no teste — cópia concorda com ela mesma para
 * sempre.
 */
function monthQuery(db: Firestore, month: ReferenceMonth): Query {
  return query(collection(db, invoicesPath()), where('referenceMonth', '==', month))
}

/**
 * O registro que `firestore-indexes.test.ts` varre. **Toda consulta com
 * `where`/`orderBy` em `src/adapters/` precisa estar num destes registros** — o
 * guarda conta as chamadas de `query()` no diretório inteiro e compara com o
 * tamanho deles, justamente para que uma consulta nova não passe sem alguém
 * derivar o índice dela.
 */
export const INVOICE_QUERIES: Record<string, (db: Firestore, month: ReferenceMonth) => Query> = {
  listByMonth: monthQuery,
}

export function createFirebaseInvoiceRepository(db: Firestore): InvoiceRepository {
  return {
    listByMonth: async (month) => {
      const snap = await getDocs(monthQuery(db, month))
      return snap.docs
        .map((invoice) => toInvoice(invoice.id, invoice.data()))
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
    },
  }
}
